import React, { useState } from 'react';
import { outlineApi } from '../../api/outlines';
import Modal from '../Common/Modal';
import Button from '../Common/Button';
import './Outline.css';

// Story-level section types (nested structure supported)
const STORY_SECTION_TYPES = [
  { value: 'chapter', label: 'Chapter', description: 'A chapter in your novel' },
  { value: 'act', label: 'Act', description: 'A major structural division' },
  { value: 'sequence', label: 'Sequence', description: 'A group of related scenes' },
  { value: 'scene', label: 'Scene', description: 'A single scene' },
  { value: 'beat', label: 'Moment', description: 'One thing that happens in a scene.' },
];

// Project-level section types (flat structure - no nesting)
const PROJECT_SECTION_TYPES = [
  { value: 'book', label: 'Book', description: 'A book in your series' },
];

/**
 * AddSectionModal - Add a new section to an outline.
 *
 * Props:
 * - outlineId: UUID of the outline
 * - parentSection: Parent section (null for root level, ignored for project-level)
 * - isProjectLevel: Whether this is a project-level outline
 * - onClose: Close callback
 * - onCreated: Callback with created section
 */
export function AddSectionModal({ outlineId, parentSection, isProjectLevel = false, onClose, onCreated }) {
  // Use appropriate section types based on scope
  const sectionTypes = isProjectLevel ? PROJECT_SECTION_TYPES : STORY_SECTION_TYPES;
  const defaultType = isProjectLevel ? 'book' : (parentSection ? 'scene' : 'chapter');

  const [displayName, setDisplayName] = useState('');
  const [sectionType, setSectionType] = useState(defaultType);
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setCreating(true);
      setError(null);
      const response = await outlineApi.createSection(outlineId, {
        section_type: sectionType,
        display_name: displayName.trim(),
        description: description.trim() || null,
        // Project-level outlines don't support nesting
        parent_section_id: isProjectLevel ? null : (parentSection?.id || null)
      });
      onCreated(response.data);
    } catch (err) {
      setError('Failed to create section');
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const modalTitle = isProjectLevel ? 'Add Series Section' : 'Add Section';
  const namePlaceholder = isProjectLevel
    ? 'e.g., Book 1: The Beginning'
    : 'e.g., Chapter 1: The Beginning';

  return (
    <Modal isOpen={true} onClose={onClose} title={modalTitle}>
      <form onSubmit={handleSubmit} className="add-section-modal">
        {!isProjectLevel && parentSection && (
          <div className="add-section-modal__parent-info">
            <span>Adding sub-section to:</span>
            <strong>{parentSection.display_name}</strong>
          </div>
        )}

        {error && <div className="add-section-modal__error">{error}</div>}

        <div className="add-section-modal__field">
          <label htmlFor="section-name">Section Name</label>
          <input
            id="section-name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={namePlaceholder}
            autoFocus
          />
        </div>

        <div className="add-section-modal__field">
          <label htmlFor="section-type">Section Type</label>
          <select
            id="section-type"
            value={sectionType}
            onChange={(e) => setSectionType(e.target.value)}
          >
            {sectionTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label} - {type.description}
              </option>
            ))}
          </select>
        </div>

        <div className="add-section-modal__field">
          <label htmlFor="section-description">Description (optional)</label>
          <textarea
            id="section-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What should happen in this section?"
            rows={3}
          />
        </div>

        <div className="add-section-modal__actions">
          <Button type="button" onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={creating} variant="primary">
            {creating ? 'Creating...' : 'Add Section'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default AddSectionModal;
